import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useEventBrand } from '../../store';

interface Page {
  prefix: string;
  title: string;
  subtitle: string;
}

// Routes that show a page title + subtitle instead of the app brand.
const PAGES: Page[] = [
  {
    prefix: '/manager',
    title: 'Device Manager',
    subtitle: 'Encoders linked across all organizations · status, network health & software'
  },
  {
    prefix: '/installers',
    title: 'Installers',
    subtitle: 'Signed installer packages · the versions devices update to'
  }
];

const Title = styled(Link)`
  color: var(--white);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.4px;
  line-height: 1.1;
  white-space: nowrap;

  &:hover,
  &:focus {
    color: var(--white);
    text-decoration: none;
    opacity: 0.85;
  }
`;

const PageTitle = styled.div`
  min-width: 0;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin: 0;
    max-width: 460px;
    color: var(--white);
    font-size: 19px;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.3px;
    white-space: nowrap;
    text-decoration: none;
  }

  .title .label {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    font-size: 19px;
    font-weight: 700;
    margin:0px;
  }

  .title i {
    font-size: 13px;
    color: rgba(176, 186, 209, 0.7);
    flex-shrink: 0;
  }

  a.title:hover,
  a.title:focus {
    color: var(--white);
    text-decoration: none;
    opacity: 0.85;
  }

  .subtitle {
    margin: 5px 0 0;
    color: rgba(176, 186, 209, 0.6);
    font-size: 12px;
    font-weight: 500;
  }
`;

/*
 * Header brand. On the manager/installers routes it renders a page title with a
 * subtitle; everywhere else it shows the app brand, which links back to root.
 */
export const Brand = (): ReactElement => {
  const { pathname } = useLocation();
  const brand = useEventBrand((state) => state.brand);

  // When viewing an event, its layout publishes the event name/location/stream link.
  if (brand) {
    return (
      <PageTitle>
        {brand.link ? (
          <a className='title' href={brand.link} target='_blank' rel='noreferrer'>
            <span className='label'>{brand.title}</span>
            <i className='far fa-external-link' />
          </a>
        ) : (
          <h1 className='title'>{brand.title}</h1>
        )}
        {brand.subtitle !== undefined && <p className='subtitle'>{brand.subtitle}</p>}
      </PageTitle>
    );
  }

  const page = PAGES.find((p) => pathname === p.prefix || pathname.startsWith(`${p.prefix}/`));

  if (page) {
    return (
      <PageTitle>
        <h1 className='title'>{page.title}</h1>
        <p className='subtitle'>{page.subtitle}</p>
      </PageTitle>
    );
  }

  return <Title to='/'>Command Center</Title>;
};
